import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import FeeScheduleTemplateFilterSchema from "work/fee-schedule-templates/_api/queries/contracts/filtering/_schema/fee-schedule-template-filter-schema";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import FeeScheduleTemplateOrderSchema from "work/fee-schedule-templates/_api/queries/contracts/ordering/_schema/fee-schedule-template-order-schema";
import { FeeScheduleTemplateOrderParameters } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import z from "zod";

export type FeeScheduleTemplateQueryJSON = z.infer<typeof FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ>;

export type FeeScheduleTemplateQueryParams = z.infer<typeof FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryZ>;

export default class FeeScheduleTemplateQuerySchema {
  public static readonly FeeScheduleTemplateQueryJSONZ = z.object({
    filterParameters: FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterJSONZ,
    orderParameters: FeeScheduleTemplateOrderSchema.FeeScheduleTemplateOrderJSONZ,
    paginationParameters: PaginationSchema.PaginationZ.nullish(),
  });

  public static readonly FeeScheduleTemplateQueryZ = z.object({
    filterParameters: FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterZ,
    orderParameters: FeeScheduleTemplateOrderSchema.FeeScheduleTemplateOrderZ,
    paginationParameters: PaginationSchema.PaginationZ.optional(),
  });

  public static validate(values: unknown): FeeScheduleTemplateQueryParams {
    return parseOrThrow(FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): FeeScheduleTemplateQueryJSON {
    return parseOrThrow(FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is FeeScheduleTemplateQueryJSON {
    try {
      FeeScheduleTemplateQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleTemplateQueryJSON): FeeScheduleTemplateQueryParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return FeeScheduleTemplateQuerySchema.codec.decode(parsed);
  }

  public static serialize(params: FeeScheduleTemplateQueryParams): FeeScheduleTemplateQueryJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = FeeScheduleTemplateQuerySchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeScheduleTemplateQuerySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ,
    FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryZ,
    {
      decode: (data) => ({
        filterParameters: FeeScheduleTemplateFilterParameters.fromJSON(data.filterParameters),
        orderParameters: FeeScheduleTemplateOrderParameters.fromJSON(data.orderParameters),
        paginationParameters: data.paginationParameters
          ? PaginationParameters.fromJSON(data.paginationParameters)
          : undefined,
      }),
      encode: (query) => ({
        filterParameters: FeeScheduleTemplateFilterSchema.serialize(query.filterParameters),
        orderParameters: FeeScheduleTemplateOrderSchema.serialize(query.orderParameters),
        paginationParameters: query.paginationParameters
        ? PaginationSchema.serialize(query.paginationParameters)
        : undefined,
      }),
    }
  );
}
